'use client';

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface PanelSectionLabelProps {
  /** A 12–14px glyph before the text, when the section has one. */
  icon?: ReactNode;
  /** Spacing and, where a tinted card needs it, colour. */
  className?: string;
  children: ReactNode;
}

/**
 * The one way a detail panel names a section.
 *
 * There were twelve of these across the Tasks panel and the shared priority card, and
 * measuring them on production found **four distinct styles in a single panel** —
 * 11px/600 at 0.66px, 0.6px and 0.55px of tracking, plus a 10px/500 at 0.25px. Nothing
 * chose those differences; they are what twelve hand-written class strings drift into.
 * Three of them differ by hundredths of a pixel, which no reader can see as intent and
 * every reader feels as noise.
 *
 * The values are the Linear surface's `.section-title` (12px/16px/500, sentence case, 6px
 * icon gap) expressed in Tailwind, because these labels sit outside `.mb-surface` — the
 * aside's panels keep their own markup, only the header band is in scope. `/signals` has
 * had exactly one of these all along; this is the same one.
 *
 * **Not `text-xs`.** `tailwind.config.js` redefines that step as `0.625rem/12px`, so in this
 * repo it is **10px**, not Tailwind's 12px — which is why `audience-detail.css` raises
 * `[class*='text-xs']` to 12px and why writing it here would have made four 11px labels
 * *smaller* instead of quieter. `text-s` is 12px but on a 14px line. The explicit pair is
 * the only way to land on the reference rule exactly.
 *
 * `mb-panel-section-label` is a styling hook, not decoration: three surfaces (Today,
 * /prospects, the audiences contact panel) deliberately set their own register for an
 * embedded card's labels, and they used to reach them through `[class*='uppercase']` —
 * a selector that silently stopped matching the moment the capitals went away. The class
 * is a name those rules can key on that cannot drift out from under them.
 *
 * Sentence case, not caps: `/signals` has no uppercase anywhere, and an earlier round
 * already made this call for the Tasks tab strip, where 10px/600 caps were the loudest
 * thing in a panel whose body text is 13px.
 *
 * Two spacings rather than five: `mb-1.5` when the label sits directly on a control it
 * names, `mb-2.5` when it opens a card or a list. The five that existed (3, 2.5, 2, 1.5)
 * were the same drift in the other direction.
 */
export const PanelSectionLabel = ({ icon, className, children }: PanelSectionLabelProps) => (
  <div className={cn('mb-panel-section-label flex items-center gap-1.5 text-[12px] leading-4 font-medium text-slate-500', className)}>
    {icon}
    <span className="min-w-0">{children}</span>
  </div>
);
